// These functions are for retrieving the position of elements, taking scrolling into account.

export type xyCoord = {
  x: number; // x position
  y: number; // y position
};
export type xyPosition = {
  x: number; // x position
  y: number; // y position
  relx?: number; // relative x position as % in container
  rely?: number; //  relative y position as % in container
  invx?: number; // x position from the right of the container
  invy?: number; // y position from the bottom of the container
  cw?: number; // width of the container
  ch?: number; // height of the container
};
export type xyzPosition = xyPosition & {
  z?: number; // z position
};
export const xyOrigin: xyPosition = { x: 0, y: 0, relx: 0, rely: 0 };
export const xyzOrigin: xyzPosition = { x: 0, y: 0, z: 0, relx: 0, rely: 0 };
export const LeftRightBoundary = 25;

export function isConsideredLeft(pos: xyPosition): boolean {
  if (!pos?.relx) return false;
  return pos && pos.relx <= LeftRightBoundary;
}

export function getPos(
  element: HTMLDivElement | xyPosition | null | undefined,
): xyPosition {
  if (!element) return xyOrigin;
  if (element instanceof HTMLDivElement) {
    const rect = element.getBoundingClientRect();
    return {
      x: rect.x + window.scrollX,
      y: rect.y + window.scrollY,
      relx: 0,
      rely: 0,
    };
  }
  return element;
}

export function addRelativePosInPercentage(
  pos: xyPosition,
  container: HTMLDivElement | xyPosition | null | undefined,
) {
  if (!pos || !container || !(container instanceof HTMLDivElement)) return pos;
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  const relx = (100 * pos.x) / cw;
  const rely = (100 * pos.y) / ch;
  return { ...pos, relx, rely, cw, ch };
}

export function addRelativePosFromBottomRight(
  pos: xyPosition,
  container: HTMLDivElement | xyPosition | null | undefined,
) {
  if (!pos || !container || !(container instanceof HTMLDivElement)) return pos;
  const cw = container.clientWidth;
  const ch = container.clientHeight;
  const invx = container.clientWidth - pos.x;
  const invy = container.clientHeight - pos.y;
  return { ...pos, invx, invy, cw, ch };
}

export function relativePos(
  element: HTMLDivElement | xyPosition | null,
  container: HTMLDivElement | xyPosition | null,
): xyPosition {
  const e = getPos(element);
  const c = getPos(container);
  if (e && c) {
    let p = subtractPos(e, c);
    p = addRelativePosInPercentage(p, container);
    p = addRelativePosFromBottomRight(p, container);
    return p;
  }
  return xyOrigin;
}

export function subtractPos(a: xyPosition, b: xyPosition): xyPosition {
  if (a && b)
    return {
      x: a.x - b.x,
      y: a.y - b.y,
    };
  return xyOrigin;
}

export function getTouchCenter(touch1: Touch, touch2: Touch) {
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
}

export function getTouchDistance(touch1: Touch, touch2: Touch) {
  return Math.hypot(
    touch1.clientX - touch2.clientX,
    touch1.clientY - touch2.clientY,
  );
}

export function getCenter(coord1: xyCoord, coord2: xyCoord) {
  return {
    x: (coord1.x + coord2.x) / 2,
    y: (coord1.y + coord2.y) / 2,
  };
}

export function getDistance(coord1: xyCoord, coord2: xyCoord) {
  return Math.hypot(coord1.x - coord2.x, coord1.y - coord2.y);
}

export function positionProps(position: xyzPosition) {
  return {
    'data-pd-x': position.x,
    'data-pd-y': position.y,
    'data-pd-z': position.z,
  };
}

// Get translation based on offset and scale
export function getTranslateCSS(
  position: xyCoord,
  offset: xyCoord,
  scale: number,
  includeScale?: boolean,
) {
  // Calculate the element's position in screen coordinates relative to its original position.
  const screenX = offset.x + position.x * scale;
  const screenY = offset.y + position.y * scale;
  return `translate(${screenX - position.x}px, ${screenY - position.y}px) ${includeScale ? 'scale(' + scale + ')' : ''}`;
}

// Translate an element based on offset and scale, but do not scale the element
export function translateElement(
  element: HTMLElement,
  offset: { x: number; y: number },
  scale?: number,
) {
  // Retrieve the element's original (absolute) coordinates from its style attributes.
  const translation = getTranslateCSS(
    {
      x: parseFloat(element.style.left || '0'),
      y: parseFloat(element.style.top || '0'),
    },
    offset,
    scale ?? 1,
    false,
  );
  // Set the transform so that the element's position updates, but its size remains constant.
  element.style.transform = translation;
  element.style.transformOrigin = 'top left';
}

// Get left and top props based on offset and scale
export function getTopLeftProps(
  position: xyCoord,
  offset: xyCoord,
  scale: number,
  extraOffset?: xyCoord,
): xyCoord {
  // scale coordinates
  const scaledX = position.x * scale;
  const scaledY = position.y * scale;
  // offset coordinates
  const screenX = scaledX + offset.x;
  const screenY = scaledY + offset.y;
  const extra = extraOffset ?? { x: 0, y: 0 };
  // add extra offset
  const left = screenX - extra.x;
  const top = screenY - extra.y;
  const pos = {
    x: left,
    y: top,
  };
  return pos;
}

// Translate an element based on offset and scale, but do not scale the element
export function positionElement(
  element: HTMLElement,
  offset: { x: number; y: number },
  scale?: number,
  extraOffset?: xyCoord,
) {
  // Retrieve the element's original (absolute) coordinates from its style attributes.
  const x = parseFloat(
    // element.getAttribute('data-pd-x') || element.style.left || '0',
    element.getAttribute('data-pd-x') || '0',
  );
  const y = parseFloat(
    // element.getAttribute('data-pd-y') || element.style.top || '0',
    element.getAttribute('data-pd-y') || '0',
  );
  const topLeft = getTopLeftProps({ x, y }, offset, scale ?? 1, extraOffset);
  // Set the transform so that the element's position updates, but its size remains constant.
  element.style.left = `${topLeft.x}px`;
  element.style.top = `${topLeft.y}px`;
  // element.style.transform = 'unset';
}

// Translate an element based on offset and scale, but do not scale the element
export function translateAndScaleElement(
  element: HTMLElement,
  offset: { x: number; y: number },
  scale: number,
) {
  if (!element) return;
  // Retrieve the element's original (absolute) coordinates from its style attributes.
  const translation = getTranslateCSS(
    {
      x: parseFloat(element.style.left || '0'),
      y: parseFloat(element.style.top || '0'),
    },
    offset,
    scale,
    true,
  );
  // Set the transform so that the element's position updates, but its size remains constant.
  element.style.transform = translation;
  element.style.transformOrigin = 'top left';
}
